import React, { useState, useMemo } from 'react';
import { Search, CheckSquare, Square, Globe, Music, Download, Users, Radio, Gamepad2 } from 'lucide-react';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { allStores } from '../../data/mockData';
import { StoreCategory } from '../../types';

interface StoreSelectorProps {
  selectedStores: string[];
  onChange: (stores: string[]) => void;
}

export const StoreSelector: React.FC<StoreSelectorProps> = ({
  selectedStores,
  onChange
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<StoreCategory | 'all'>('all');
  const [selectAll, setSelectAll] = useState(false);

  const categories = [
    { id: 'all' as const, label: 'All Stores', icon: Globe },
    { id: 'streaming' as const, label: 'Streaming', icon: Music },
    { id: 'download' as const, label: 'Download', icon: Download },
    { id: 'social' as const, label: 'Social Media', icon: Users },
    { id: 'radio' as const, label: 'Radio', icon: Radio },
    { id: 'gaming' as const, label: 'Gaming', icon: Gamepad2 }
  ];

  const filteredStores = useMemo(() => {
    return allStores.filter(store => {
      const matchesSearch = store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           store.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || store.category === selectedCategory;
      return matchesSearch && matchesCategory && store.isActive;
    });
  }, [searchQuery, selectedCategory]);

  const handleStoreToggle = (storeId: string) => {
    if (selectedStores.includes(storeId)) {
      onChange(selectedStores.filter(id => id !== storeId));
    } else {
      onChange([...selectedStores, storeId]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      onChange([]);
      setSelectAll(false);
    } else {
      const allActiveStores = filteredStores.map(store => store.id);
      onChange(allActiveStores);
      setSelectAll(true);
    }
  };

  const getCategoryIcon = (category: StoreCategory) => {
    const categoryData = categories.find(cat => cat.id === category);
    return categoryData?.icon || Globe;
  };

  const getCategoryColor = (category: StoreCategory) => {
    switch (category) {
      case 'streaming': return 'text-green-400 bg-green-500/10';
      case 'download': return 'text-blue-400 bg-blue-500/10';
      case 'social': return 'text-purple-400 bg-purple-500/10';
      case 'radio': return 'text-orange-400 bg-orange-500/10';
      case 'gaming': return 'text-red-400 bg-red-500/10';
      default: return 'text-gray-400 bg-gray-500/10';
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search stores..."
            value={searchQuery}
            onChange={setSearchQuery}
            icon={Search}
          />
        </div>
        <Button
          onClick={handleSelectAll}
          variant={selectAll ? 'primary' : 'outline'}
          className="md:w-auto"
        >
          {selectAll ? 'Deselect All' : 'Select All'}
        </Button>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => {
          const Icon = category.icon;
          const isSelected = selectedCategory === category.id;
          
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm
                transition-all duration-200
                ${isSelected 
                  ? 'gold-gradient text-black' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }
              `}
            >
              <Icon className="w-4 h-4" />
              <span>{category.label}</span>
            </button>
          );
        })}
      </div>

      {/* Store Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredStores.map((store) => {
          const isSelected = selectedStores.includes(store.id);
          const Icon = getCategoryIcon(store.category);
          const categoryColor = getCategoryColor(store.category);
          
          return (
            <div
              key={store.id}
              className={`cursor-pointer transition-all duration-200 p-3 rounded-xl border ${
                isSelected 
                  ? 'border-gold-500 bg-gold-500/5' 
                  : 'border-gray-700 hover:border-gray-600 bg-black-800'
              }`}
              onClick={() => handleStoreToggle(store.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{store.logo}</span>
                    <div className={`p-1 rounded ${categoryColor}`}>
                      <Icon className="w-3 h-3" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-white text-sm">{store.name}</p>
                    <p className="text-xs text-gray-400">{store.processingTime}</p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {isSelected ? (
                    <CheckSquare className="w-5 h-5 text-gold-400" />
                  ) : (
                    <Square className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                {store.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Selection Summary */}
      <Card className="bg-gold-500/5 border-gold-500/20">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gold-400">
              {selectedStores.length} stores selected
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Your music will be distributed to {selectedStores.length} platforms
            </p>
          </div>
          {selectedStores.length > 0 && (
            <Button
              variant="ghost"
              onClick={() => onChange([])}
              className="text-gray-400 hover:text-white"
            >
              Clear All
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};